import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { LogoMark } from '../components/Logo';
import { useProducts } from '../product/ProductContext';
import { getSetupSteps, isOnboarding, productInitial } from '../product/productUtils';
import { supabase } from '../supabase';
import { markLeaving } from '../auth/leaving';
import { useBilling } from '../billing/BillingContext';
import UsageMeter from '../billing/UsageMeter';
import { clearCached } from '../billing/format';

// Nav ids match the /dashboard/:section routes
export const NAV_GROUPS = [
    { title: '', items: [{ id: 'dashboard', icon: 'space_dashboard', label: 'Dashboard' }] },
    {
        title: 'Create',
        items: [
            { id: 'social', icon: 'chat_bubble', label: 'Social posts' },
            { id: 'blog', icon: 'article', label: 'Blog articles' },
            { id: 'ads', icon: 'campaign', label: 'Ad copy' },
            { id: 'video', icon: 'movie', label: 'Video scripts' },
        ],
    },
    {
        title: 'Plan',
        items: [
            { id: 'plan', icon: 'calendar_month', label: 'Content plan' },
            { id: 'publishing', icon: 'send', label: 'Publishing' },
            { id: 'whiteboard', icon: 'draw', label: 'Whiteboard' },
        ],
    },
    {
        title: 'Measure',
        items: [
            { id: 'links', icon: 'link', label: 'Tracked links' },
            { id: 'reports', icon: 'monitoring', label: 'Reports' },
        ],
    },
    {
        title: 'Product',
        items: [
            { id: 'profile', icon: 'inventory_2', label: 'Product profile' },
            { id: 'icps', icon: 'groups', label: 'ICPs', badge: 'icps' },
            { id: 'voice', icon: 'record_voice_over', label: 'Brand voice' },
            { id: 'channels', icon: 'hub', label: 'Social accounts' },
            { id: 'memory', icon: 'psychology', label: 'Memory' },
        ],
    },
    {
        title: 'Account',
        items: [
            { id: 'billing', icon: 'credit_card', label: 'Plans & billing' },
            { id: 'notifications', icon: 'notifications', label: 'Email updates' },
        ],
    },
];

// The product's logo, or its initial on its colour when it has none (or the logo won't load)
export const ProductTile = ({ product, size = 34, radius = 9, fontSize = 15 }) => {
    const [failedLogo, setFailedLogo] = useState('');
    const logo = product?.logo && product.logo !== failedLogo ? product.logo : '';
    return (
        <div
            className={`ls-tile${logo ? ' has-logo' : ''}`}
            style={{ width: size, height: size, borderRadius: radius, fontSize, background: logo ? undefined : product?.color || '#98a2b3' }}
        >
            {logo
                ? <img src={logo} alt="" width={size} height={size} draggable="false" onError={() => setFailedLogo(product.logo)} />
                : productInitial(product)}
        </div>
    );
};

// "Setup 3 of 5", "Budgeting app · Ready" or "Setup 1 of 5 · Resume"
export const productStatus = (product, icpCount) => {
    const steps = getSetupSteps(product, icpCount);
    const done = steps.filter(step => step.done).length;
    if (isOnboarding(product)) return { text: `Setup ${done} of ${steps.length} · Resume`, resume: true };
    if (done === steps.length) return { text: `${product.category || 'Product'} · Ready` };
    return { text: `Setup ${done} of ${steps.length}` };
};

const userName = (user) =>
    user?.user_metadata?.full_name || user?.user_metadata?.name || (user?.email || 'You').split('@')[0];

const userInitials = (user) => {
    const words = userName(user).split(/[\s._-]+/).filter(Boolean);
    return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase() || '?';
};

// This month's generations, with the way to get more one click away
const UsageCard = ({ active, onOpen }) => {
    const { account, usage, pricing, openUpgrade } = useBilling() || {};
    if (!usage || !account) return null;
    const isFree = account.plan === 'free';
    const nearLimit = usage.generations.used >= usage.generations.limit * 0.8;
    // Paid users only see this once it matters
    if (!isFree && !nearLimit && !account.paymentIssue) return null;
    const planName = pricing?.plans.find(p => p.id === account.plan)?.name || 'Free';
    return (
        <div className="ls-sidebar-usage">
            <UsageMeter compact label={`${planName} · generations`} {...usage.generations} />
            {account.paymentIssue ? (
                <button type="button" className="ls-btn ls-btn-secondary" onClick={() => onOpen('/dashboard/billing')}>
                    <Icon name="credit_card_off" size={16} />Fix payment
                </button>
            ) : isFree ? (
                <button type="button" className="ls-btn ls-btn-primary" onClick={() => openUpgrade()}>
                    <Icon name="rocket_launch" size={16} />Upgrade
                </button>
            ) : active !== 'billing' && (
                <button type="button" className="ls-btn ls-btn-secondary" onClick={() => onOpen('/dashboard/billing')}>
                    Get more
                </button>
            )}
        </div>
    );
};

const Sidebar = ({ user, active, isLoading = false, onSwitcherToggle, onNavigate }) => {
    const navigate = useNavigate();
    const { products, currentProduct, icps, selectProduct } = useProducts();
    const { usage, openUpgrade } = useBilling() || {};
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [accountAnchor, setAccountAnchor] = useState(null);
    const switcherRef = useRef(null);

    const setOpen = (open) => {
        setSwitcherOpen(open);
        onSwitcherToggle?.(open);
    };

    // Click outside or Escape closes the switcher
    useEffect(() => {
        if (!switcherOpen) return undefined;
        const handleClick = (event) => {
            if (switcherRef.current && !switcherRef.current.contains(event.target)) setOpen(false);
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [switcherOpen]);

    const go = (path) => {
        setOpen(false);
        onNavigate?.();
        navigate(path);
    };

    const handleProduct = (product) => {
        if (isOnboarding(product)) {
            go(`/products/${product.id}/setup`);
            return;
        }
        selectProduct(product.id);
        go('/dashboard');
    };

    const handleSignOut = async () => {
        try {
            markLeaving();
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            clearCached();
            window.location.replace('/');
        } catch (error) {
            console.error('Error signing out:', error.message);
            alert(error.message);
        }
    };

    const current = currentProduct;
    const currentStatus = current && productStatus(current, icps.length);

    return (
        <aside className="ls-sidebar">
            <div ref={switcherRef} style={{ position: 'relative' }}>
                <button
                    type="button"
                    className="ls-switcher"
                    onClick={() => setOpen(!switcherOpen)}
                    disabled={isLoading}
                    aria-busy={isLoading}
                    aria-haspopup="menu"
                    aria-expanded={switcherOpen}
                >
                    {isLoading ? (
                        <>
                            <div className="ls-skeleton" style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="ls-switcher-name"><span className="ls-skeleton-text" style={{ width: 110 }} /></div>
                                <div className="ls-switcher-sub"><span className="ls-skeleton-text" style={{ width: 70 }} /></div>
                            </div>
                        </>
                    ) : (
                        <>
                            <ProductTile product={current} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="ls-switcher-name">{current ? current.name : 'No product yet'}</div>
                                <div className="ls-switcher-sub">
                                    {current ? (current.category || currentStatus.text) : 'Add one to get started'}
                                </div>
                            </div>
                        </>
                    )}
                    <Icon name="unfold_more" color="var(--muted)" />
                </button>

                {switcherOpen && (
                    <div className="ls-popover" role="menu">
                        <div className="ls-popover-title">Products</div>
                        {products.map(product => {
                            const isCurrent = product.id === current?.id;
                            const status = productStatus(product, isCurrent ? icps.length : product.icpCount);
                            return (
                                <button
                                    type="button"
                                    role="menuitem"
                                    key={product.id}
                                    className={`ls-popover-row${isCurrent ? ' is-current' : ''}`}
                                    onClick={() => handleProduct(product)}
                                >
                                    <ProductTile product={product} size={30} radius={8} fontSize={13} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="ls-switcher-name">{product.name}</div>
                                        <div className="ls-switcher-sub" style={status.resume ? { color: '#b54708' } : undefined}>
                                            {status.text}
                                        </div>
                                    </div>
                                    {isCurrent && <Icon name="check" color="var(--accent-strong)" />}
                                </button>
                            );
                        })}
                        {products.length > 0 && <div className="ls-popover-divider" />}
                        <button
                            type="button"
                            role="menuitem"
                            className="ls-popover-row"
                            style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-text)' }}
                            onClick={() => {
                                // At the plan's product limit, show what unlocks more instead of a setup that can't be saved
                                if (usage && products.length >= usage.products.limit) {
                                    setOpen(false);
                                    openUpgrade({ metric: 'products', limit: usage.products.limit, used: products.length });
                                    return;
                                }
                                go('/products/new');
                            }}
                        >
                            <Icon name="add" />Add product
                        </button>
                        {products.length > 0 && (
                            <button
                                type="button"
                                role="menuitem"
                                className="ls-popover-row"
                                style={{ fontSize: 14, color: 'var(--secondary)' }}
                                onClick={() => go('/dashboard/products')}
                            >
                                <Icon name="settings" />Manage products
                            </button>
                        )}
                    </div>
                )}
            </div>

            <nav className="ls-nav" aria-label="Main">
                {NAV_GROUPS.map(group => (
                    <div className="ls-nav-group" key={group.title || 'home'}>
                        {group.title && <div className="ls-nav-title">{group.title}</div>}
                        {group.items.map(item => (
                            <button
                                type="button"
                                key={item.id}
                                className={`ls-nav-item${active === item.id ? ' is-active' : ''}`}
                                aria-current={active === item.id ? 'page' : undefined}
                                onClick={() => go(item.id === 'dashboard' ? '/dashboard' : `/dashboard/${item.id}`)}
                            >
                                <Icon name={item.icon} size={19} />
                                <span style={{ flex: 1 }}>{item.label}</span>
                                {item.badge === 'icps' && icps.length > 0 && <span className="ls-badge">{icps.length}</span>}
                            </button>
                        ))}
                    </div>
                ))}
            </nav>

            {!isLoading && <UsageCard active={active} onOpen={go} />}

            <button type="button" className="ls-sidebar-footer" onClick={(event) => setAccountAnchor(event.currentTarget)}>
                <div className="ls-avatar" style={{ width: 30, height: 30, background: 'var(--subtle)', color: 'var(--ink)', fontSize: 12, fontWeight: 600 }}>
                    {user && userInitials(user)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ls-switcher-name" style={{ fontSize: 13 }}>
                        {user ? userName(user) : <span className="ls-skeleton-text" style={{ width: 90 }} />}
                    </div>
                    <div className="ls-switcher-sub">Account</div>
                </div>
                <LogoMark size={22} />
            </button>
            <Popover
                open={Boolean(accountAnchor)}
                anchorEl={accountAnchor}
                onClose={() => setAccountAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { style: { border: '1px solid #e7eaf0', borderRadius: 12, padding: 8, minWidth: 220, boxShadow: '0 18px 48px rgba(16,24,40,.18)' } } }}
            >
                <div style={{ padding: '8px 10px', fontSize: 13, color: '#667085', fontFamily: 'var(--font-sans)' }}>{user?.email}</div>
                <button
                    type="button"
                    className="ls-popover-row"
                    style={{ fontSize: 14, fontFamily: 'var(--font-sans)' }}
                    onClick={() => { setAccountAnchor(null); go('/dashboard/billing'); }}
                >
                    <span className="ls-icon" style={{ fontSize: 20 }}>credit_card</span>Plans &amp; billing
                </button>
                <button type="button" className="ls-popover-row" style={{ fontSize: 14, fontFamily: 'var(--font-sans)' }} onClick={handleSignOut}>
                    <span className="ls-icon" style={{ fontSize: 20 }}>logout</span>Sign out
                </button>
            </Popover>
        </aside>
    );
};

export default Sidebar;
